import {appApi} from "@src/integrations/redux/app-api";
import {
	type InviteFirebaseErrorRequest,
	type SendPhoneOtpRequest,
	type SendInvitePhoneOtpRequest,
	type VerifyPhoneOtpRequest,
	type VerifyPhoneOtpResponse,
	type VerifyInvitePhoneRequest,
} from "@src/features/auth/auth-dtos";
import {type AuthState} from "@src/features/auth/auth-models";
import {HTTP_METHODS} from "@src/utils/network-constants";


export const authApi = appApi.injectEndpoints({
	endpoints: (builder) => ({
		sendPhoneOtp: builder.mutation<void, SendPhoneOtpRequest>({
			query(args) {
				return {
					url: "/api/v1/auth/sendPhoneOtp",
					method: HTTP_METHODS.POST,
					body: args
				}
			},
		}),
		verifyPhoneOtp: builder.mutation<AuthState, VerifyPhoneOtpRequest>({
			query(args) {
				return {
					url: "/api/v1/auth/verifyPhoneOtp",
					method: HTTP_METHODS.POST,
					body: args
				}
			},
			transformResponse(response: VerifyPhoneOtpResponse) {
				const authState = {
					userFullInfo: response.userFullInfo,
					userRoles: response.userRoles,
					spacesPublicInfo: response.spacesPublicInfo
				} satisfies AuthState;
				return authState;
			}
		}),
		sendInvitePhoneOtp: builder.mutation<void, SendInvitePhoneOtpRequest>({
			query: (body) => ({
				url: "/api/v1/auth/invite/sendPhoneOtp",
				method: HTTP_METHODS.POST,
				body,
			}),
		}),
		checkInviteEligibility: builder.mutation<void, string>({
			query: (groupId) => ({
				url: `/api/v1/auth/invite/${encodeURIComponent(groupId)}/eligibility`,
				method: HTTP_METHODS.GET,
			}),
		}),
		joinInviteWithCurrentSession: builder.mutation<AuthState, string>({
			query: (groupId) => ({
				url: `/api/v1/auth/invite/${encodeURIComponent(groupId)}/join`,
				method: HTTP_METHODS.POST,
				body: {},
			}),
		}),
		inviteContext: builder.query<{session: AuthState | null; groupName: string; canJoin: boolean}, string>({
			query: (groupId) => `/api/v1/auth/invite/${encodeURIComponent(groupId)}/context`,
			keepUnusedDataFor: 60,
		}),
		inviteLogOut: builder.mutation<void, void>({
			query: () => ({url: '/api/v1/auth/invite/logOut', method: HTTP_METHODS.POST, body: {}}),
		}),
		verifyInvitePhone: builder.mutation<AuthState, VerifyInvitePhoneRequest>({
			query: (body) => ({
				url: "/api/v1/auth/invite/verifyPhone",
				method: HTTP_METHODS.POST,
				body,
			}),
			transformResponse(response: VerifyPhoneOtpResponse) {
				return response satisfies AuthState;
			},
		}),
		logInviteFirebaseError: builder.mutation<void, InviteFirebaseErrorRequest>({
			query: (body) => ({
				url: "/api/v1/auth/invite/firebaseError",
				method: HTTP_METHODS.POST,
				body,
			}),
		}),
		refreshSession: builder.mutation<AuthState, void>({
			query: () => ({
				url: "/api/v1/auth/refreshToken",
				method: HTTP_METHODS.POST,
			}),
		}),
		logOut: builder.mutation<void, void>({
			query() {
				return {
					url: "/api/v1/auth/logOut",
					method: HTTP_METHODS.POST,
				}
			},
		})
	}),
	overrideExisting: "throw"
});

export const {
	useInviteContextQuery, useInviteLogOutMutation,
	useSendPhoneOtpMutation, useVerifyPhoneOtpMutation, useSendInvitePhoneOtpMutation,
	useCheckInviteEligibilityMutation, useJoinInviteWithCurrentSessionMutation, useVerifyInvitePhoneMutation,
	useLogInviteFirebaseErrorMutation, useRefreshSessionMutation,
	useLogOutMutation
} = authApi;
